import React, { useRef, useEffect, useState } from 'react';
import { FusedRecord } from '../../core/types/fusion';

interface TrajectoryViewProps {
  records: FusedRecord[];
}

export const TrajectoryView: React.FC<TrajectoryViewProps> = ({ records }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [colorMode, setColorMode] = useState<'time' | 'dbfs' | 'frequency'>('dbfs');
  const [useFiltered, setUseFiltered] = useState<boolean>(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#0f172a';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw Grid
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 1;
    for (let x = 0; x < canvas.width; x += 40) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    for (let y = 0; y < canvas.height; y += 40) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }

    if (records.length < 2) {
      ctx.fillStyle = '#64748b';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('等待数据积累中... 开始采集后将实时显示二维轨迹', canvas.width / 2, canvas.height / 2);
      return;
    }

    // Map 640x480 pixel space to canvas width/height
    const scaleX = canvas.width / 640;
    const scaleY = canvas.height / 480;

    for (let i = 0; i < records.length; i++) {
      const rec = records[i];
      const posA = rec.cameraAPosition;
      if (!posA || posA.status === 'lost') continue;

      const pos = useFiltered ? posA.filteredPosition : posA.rawPosition;
      const px = pos.x * scaleX;
      const py = pos.y * scaleY;

      // Color mapping
      let color = '#84cc16';
      if (colorMode === 'dbfs') {
        // Map dBFS [-100..0] to HSL hue [240 (blue) .. 0 (red)]
        const norm = Math.max(0, Math.min(1, (rec.audioFeatures.dbfs + 90) / 90));
        const hue = (1 - norm) * 240;
        color = `hsl(${hue}, 90%, 50%)`;
      } else if (colorMode === 'frequency') {
        const norm = Math.max(0, Math.min(1, rec.audioFeatures.dominantFrequencyHz / 2000));
        const hue = norm * 300;
        color = `hsl(${hue}, 90%, 50%)`;
      } else {
        const norm = i / records.length;
        const hue = norm * 260;
        color = `hsl(${hue}, 80%, 55%)`;
      }

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(px, py, 4, 0, 2 * Math.PI);
      ctx.fill();

      if (i > 0) {
        const prevRec = records[i - 1];
        const prevPosA = prevRec.cameraAPosition;
        if (prevPosA && prevPosA.status === 'tracking') {
          const prevPos = useFiltered ? prevPosA.filteredPosition : prevPosA.rawPosition;
          ctx.strokeStyle = color;
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(prevPos.x * scaleX, prevPos.y * scaleY);
          ctx.lineTo(px, py);
          ctx.stroke();
        }
      }
    }
  }, [records, colorMode, useFiltered]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>轨迹着色方案:</span>
          <button
            className={`btn btn-outline ${colorMode === 'dbfs' ? 'btn-primary' : ''}`}
            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
            onClick={() => setColorMode('dbfs')}
          >
            按声音强度 (dBFS) 着色
          </button>
          <button
            className={`btn btn-outline ${colorMode === 'frequency' ? 'btn-primary' : ''}`}
            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
            onClick={() => setColorMode('frequency')}
          >
            按主频 (Hz) 着色
          </button>
          <button
            className={`btn btn-outline ${colorMode === 'time' ? 'btn-primary' : ''}`}
            style={{ padding: '4px 10px', fontSize: '0.75rem' }}
            onClick={() => setColorMode('time')}
          >
            按时间演进 (Time) 着色
          </button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: '0.8rem', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={useFiltered}
              onChange={(e) => setUseFiltered(e.target.checked)}
              style={{ marginRight: 4 }}
            />
            显示指数平滑轨迹 (Filtered)
          </label>
        </div>
      </div>

      <div className="camera-card" style={{ width: '100%', height: '500px' }}>
        <canvas ref={canvasRef} width={800} height={500} style={{ width: '100%', height: '100%', display: 'block' }} />
      </div>
    </div>
  );
};
